import cn from 'classnames';
import { Children, type KeyboardEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../button';
import { Icon } from '../icons';
import styles from './horizontal-scroller.module.css';

export type HorizontalScrollerProperties = {
  ariaLabel?: string;
  arrows?: boolean;
  children?: ReactNode;
  className?: string;
};

const SCROLL_EDGE_THRESHOLD = 1;

const HorizontalScroller = ({ ariaLabel, arrows = true, children, className }: HorizontalScrollerProperties) => {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLUListElement>(null);
  const [{ canScrollNext, canScrollPrevious }, setScrollState] = useState({
    canScrollNext: false,
    canScrollPrevious: false,
  });

  const updateScrollState = useCallback(() => {
    const element = trackRef.current;

    if (!element) {
      return;
    }

    const maxScrollLeft = element.scrollWidth - element.clientWidth;

    setScrollState((previous) => {
      const nextState = {
        canScrollNext: element.scrollLeft < maxScrollLeft - SCROLL_EDGE_THRESHOLD,
        canScrollPrevious: element.scrollLeft > SCROLL_EDGE_THRESHOLD,
      };

      if (
        previous.canScrollPrevious === nextState.canScrollPrevious &&
        previous.canScrollNext === nextState.canScrollNext
      ) {
        return previous;
      }

      return nextState;
    });
  }, []);

  useEffect(() => {
    const element = trackRef.current;

    if (!element) {
      return;
    }

    const frameId = requestAnimationFrame(updateScrollState);
    const resizeObserver = new ResizeObserver(updateScrollState);

    resizeObserver.observe(element);
    element.addEventListener('scroll', updateScrollState, { passive: true });

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      element.removeEventListener('scroll', updateScrollState);
    };
  }, [updateScrollState]);

  useEffect(() => {
    const frameId = requestAnimationFrame(updateScrollState);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [children, updateScrollState]);

  const getScrollAmount = useCallback(() => {
    const element = trackRef.current;
    const firstItem = element?.firstElementChild;

    if (!element || !firstItem) {
      return element?.clientWidth ?? 0;
    }

    const trackStyles = getComputedStyle(element);
    const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;

    return firstItem.getBoundingClientRect().width + gap;
  }, []);

  const scrollByDirection = useCallback(
    (direction: -1 | 1) => {
      const element = trackRef.current;
      if (!element) {
        return;
      }

      const amount = getScrollAmount();
      const maxScrollLeft = element.scrollWidth - element.clientWidth;
      const nextScrollLeft = element.scrollLeft + direction * amount;

      const willReachScrollEdge =
        direction === 1
          ? nextScrollLeft >= maxScrollLeft - SCROLL_EDGE_THRESHOLD
          : nextScrollLeft <= SCROLL_EDGE_THRESHOLD;

      const focusedArrow = document.activeElement?.closest(`.${styles['horizontal-scroller__arrow']}`);

      if (willReachScrollEdge && focusedArrow) {
        element.focus();
      }

      element.scrollBy({ behavior: 'smooth', left: direction * amount });
    },
    [getScrollAmount],
  );

  const handleTrackKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      if (event.target !== event.currentTarget) {
        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollByDirection(-1);

        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollByDirection(1);
      }
    },
    [scrollByDirection],
  );

  return (
    <div
      className={cn(styles['horizontal-scroller'], className, {
        [styles['horizontal-scroller--has-next'] ?? '']: arrows && canScrollNext,
        [styles['horizontal-scroller--has-previous'] ?? '']: arrows && canScrollPrevious,
      })}
    >
      {arrows && canScrollPrevious ? (
        <Button
          aria-label="Scroll left"
          className={cn(styles['horizontal-scroller__arrow'], styles['horizontal-scroller__arrow--previous'])}
          icon={<Icon icon="arrow-left-line" />}
          onClick={() => {
            scrollByDirection(-1);
          }}
          size="small"
          type="secondary"
        />
      ) : null}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-redundant-roles -- arrow keys scroll the list; role restores list semantics in Safari when list-style is none */}
      <ul
        aria-label={ariaLabel ? t(ariaLabel) : undefined}
        className={styles['horizontal-scroller__track']}
        onKeyDown={handleTrackKeyDown}
        ref={trackRef}
        role="list"
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- arrow keys must scroll the track even when children aren't focusable
        tabIndex={0}
      >
        {Children.map(children, (child) => (
          <li>{child}</li>
        ))}
      </ul>
      {arrows && canScrollNext ? (
        <Button
          aria-label="Scroll right"
          className={cn(styles['horizontal-scroller__arrow'], styles['horizontal-scroller__arrow--next'])}
          icon={<Icon icon="arrow-right-line" />}
          onClick={() => {
            scrollByDirection(1);
          }}
          size="small"
          type="secondary"
        />
      ) : null}
    </div>
  );
};

export default HorizontalScroller;
